import type { ReactNode } from "react";

export type SegmentedOption<T extends string> = {
  value: T;
  title: string;
  description: string;
  icon?: ReactNode;
};

type Props<T extends string> = {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  name?: string;
};

export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  name,
}: Props<T>) {
  return (
    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <label
            key={opt.value}
            className={`relative flex cursor-pointer flex-col gap-2 rounded-xl border-[1.5px] p-4 transition-all ${
              active
                ? "border-primary bg-primary-soft shadow-[0_0_0_3px_rgba(91,141,239,0.12)]"
                : "border-border bg-surface hover:border-border-strong"
            }`}
          >
            <input
              type="radio"
              name={name ?? "segmented"}
              value={opt.value}
              checked={active}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            <span
              aria-hidden
              className={`absolute right-4 top-4 size-[18px] rounded-full border-2 ${
                active
                  ? "border-primary bg-primary shadow-[inset_0_0_0_3px_white]"
                  : "border-border-strong bg-surface"
              }`}
            />
            <span className="flex items-center gap-2.5 pr-6">
              {opt.icon ? (
                <span
                  className={`grid size-9 shrink-0 place-items-center rounded-md ${
                    active ? "bg-surface" : "bg-surface-alt"
                  } text-primary-hover`}
                >
                  {opt.icon}
                </span>
              ) : null}
              <span className="text-sm font-semibold text-text">{opt.title}</span>
            </span>
            <span className="text-[13px] leading-relaxed text-text-muted">
              {opt.description}
            </span>
          </label>
        );
      })}
    </div>
  );
}
